import { AccordionDetails, AccordionSummary, IconButton } from '@material-ui/core';
import MuiAccordion from '@material-ui/core/Accordion';
import cx from 'classnames';
import { icon_fold_on, icon_has_bridge, icon_trash } from 'components/base/images';
import { T } from 'components/common/text';
import CustomSpan from 'components/common/text/CustomSpan';
import { getMapperTeethNumbering } from 'lib/library';
import { indicationOptionList, teethSortValue, userCustomOption } from 'lib/mapper';
import {
  IToothShade,
  Implant,
  IndicationGroup,
  IndicationPart,
  Material,
  Tooth,
} from 'lib/types/projectTypes';
import { isArray } from 'lodash';
import React, { ChangeEvent, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UtilActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import styled from 'styled-components';
import { color } from 'styles/utils';

interface SelectedTeethListProps {
  isMultidie?: number;
  isViewer: boolean;
  numbering: number;
  teeth: any[] | null;
  bridge: string[] | null;
  selectedTeethIsExpanded: any[] | null;
  indicationInfo: any;
  materialShadeList: IToothShade[];
  onDeleteTooth?: (e: any, number: number) => void;
  onSelectedIndicationChange: (number: number) => void;
}

function SelectedTeethList({
  isMultidie,
  isViewer,
  numbering,
  teeth,
  bridge,
  selectedTeethIsExpanded,
  indicationInfo,
  materialShadeList,
  onSelectedIndicationChange,
  onDeleteTooth,
}: SelectedTeethListProps) {
  const { indicationOptionOffline, indicationOptionOfflineSuccess } = useShallowAppSelector(
    state => ({
      indicationOptionOffline: state.util.indicationOptionOffline.data,
      indicationOptionOfflineSuccess: state.util.indicationOptionOffline.success,
    }),
  );
  const { t } = useTranslation();
  const {
    groupList: indicationGroupList = [],
    partList: indicationPartList = [],
    materialList = [],
    implantList = [],
    implantAbutmentMaterialList = [],
    implantScanbodyScanList = [],
  } = indicationInfo as {
    groupList: IndicationGroup[];
    partList: IndicationPart[];
    materialList: Material[];
    implantList: Implant[];
    implantAbutmentMaterialList: any[];
    implantScanbodyScanList: any[];
  };

  const sortByTeeth = (a: Tooth, b: Tooth) => {
    const indexA = teethSortValue.indexOf(a.number);
    const indexB = teethSortValue.indexOf(b.number);
    return indexA - indexB;
  };

  useEffect(() => {
    UtilActions.fetch_teeth_indication_option_offline_request();
  }, []);

  return (
    <StyledSelectedTeethList isViewer={isViewer}>
      <div className="selectedTeethList__title">
        <T>SELECTED_TEETH_TITLE</T>
      </div>
      <div className="selectedTeethList__box">
        {useMemo(() => {
          if (!teeth || !indicationOptionOffline) return;

          const teethArrange = [...teeth].sort(sortByTeeth);

          return teethArrange?.map((item: Tooth, index: number) => {
            const implantLibrary = item.implantLibrary;
            const isExpanded = selectedTeethIsExpanded?.find(
              (expanded: any) => expanded?.number === item?.number,
            )?.isExpanded;

            if (typeof isExpanded !== 'boolean') return;
            const indicationItem = indicationPartList.find(
              (i: any) => i.idx === item.indicationIdx,
            );
            const materialItem = materialList.find((i: any) => i.idx === item.material);
            const implantItem = implantList.find((i: any) => i.idx === item.implantType);
            const groupItem = indicationGroupList.find((i: any) => i.idx === item.preparationType);
            const abutmentItem = !!item.implantAbutmentMaterialIdx
              ? implantAbutmentMaterialList.find(
                  (i: any) => i.implantAbutmentMaterialIdx === item.implantAbutmentMaterialIdx,
                )
              : null;
            const scanbodyItem = !!item.implantScanbodyScanIdx
              ? implantScanbodyScanList.find(
                  (i: any) => i.implantScanbodyScanIdx === item.implantScanbodyScanIdx,
                )
              : null;
            const materialShadeItem = materialShadeList?.find(
              (i: any) => i.seq === item.materialShade,
            )?.name;

            const detailOptionItem = [
              ...indicationOptionList.default.mainOption,
              ...indicationOptionList.default.checkBox,
              ...indicationOptionList.default.radioButton,
              ...indicationOptionList.default.slider,
              ...indicationOptionList.advanced,
            ];
            const detailOptionValue: any = { ...item?.default, ...item?.advanced };

            const detailItem = [
              {
                title: <T>PROJECT_INDICATION_TYPE</T>,
                content: t(`INDICATION_GROUP_${groupItem?.idx}`),
              },
              {
                title: <T>PROJECT_INDICATION</T>,
                content: !indicationItem ? (
                  <T>GLOBAL_UNKNOWN</T>
                ) : indicationItem?.alias.split('_')[0] === userCustomOption.indication ? (
                  indicationItem?.name
                ) : (
                  t(`INDICATION_${indicationItem?.alias}`)
                ),
              },
              {
                title: <T>PROJECT_MATERIAL</T>,
                content: !materialItem ? (
                  <T>GLOBAL_UNKNOWN</T>
                ) : materialItem?.alias.split('_')[0] === userCustomOption.material ? (
                  materialItem.name
                ) : (
                  t(`MATERIAL_${materialItem?.alias}`)
                ),
              },
              {
                title: <T>PROJECT_IMPLANT</T>,
                content: t(`ABUTMENT_IMPLANT_${implantItem?.alias}`),
              },
              {
                title: <T>PROJECT_MATERIAL_SHADE</T>,
                content: materialShadeItem,
              },
            ];

            const implantLibraryItem = [
              {
                title: <T>PHOTOGRAMMETRY_LIBRARY_COMPANY</T>,
                content: !implantLibrary ? <T>GLOBAL_UNKNOWN</T> : implantLibrary.companyName,
              },
              {
                title: <T>PHOTOGRAMMETRY_LIBRARY_IMPLANT</T>,
                content: !implantLibrary ? (
                  <T>GLOBAL_UNKNOWN</T>
                ) : (
                  implantLibrary.libraryDisplayName
                ),
              },
              {
                title: <T>PHOTOGRAMMETRY_LIBRARY_TYPE</T>,
                content: !implantLibrary ? <T>GLOBAL_UNKNOWN</T> : implantLibrary.typeDisplayName,
              },
              {
                title: <T>PHOTOGRAMMETRY_LIBRARY_SUBTYPE</T>,
                content: !implantLibrary ? (
                  <T>GLOBAL_UNKNOWN</T>
                ) : (
                  implantLibrary.subtypeDisplayName
                ),
              },
            ];

            const checkBridge = !!isViewer ? bridge : bridge;

            const hasBridge =
              isArray(checkBridge) &&
              checkBridge?.some(bridgeItem => {
                const frontNumber = String(bridgeItem).slice(0, 2);
                const backNumber = String(bridgeItem).slice(2);
                const sortedNumber = [frontNumber, backNumber].sort((a, b) => {
                  if (
                    (Number(frontNumber) > 11 && Number(frontNumber) <= 18) ||
                    (Number(frontNumber) > 31 && Number(frontNumber) <= 38)
                  ) {
                    return Number(b) - Number(a);
                  } else {
                    return Number(a) - Number(b);
                  }
                });
                return String(sortedNumber).slice(2).includes(item.number);
              });

            return (
              <div key={index}>
                <MuiAccordion
                  className="selectedTeethList__container"
                  expanded={isExpanded}
                  onChange={() => {
                    onSelectedIndicationChange(item.number);
                  }}
                >
                  <AccordionSummary
                    expandIcon={<img src={icon_fold_on} />}
                    className={cx('selectedTeethList__summary_box', {
                      isFirst: index === 0,
                      isLast: index === teeth?.length - 1,
                    })}
                  >
                    <div className="selectedTeethList__summary_item">
                      {hasBridge && (
                        <div className="selectedTeethList__bridge_icon">
                          <img src={icon_has_bridge} />
                        </div>
                      )}
                      <StyledNumberingCircle size={28} color={item.color}>
                        <CustomSpan fontWeight={500} fontColor={item.color} fontSize={14}>
                          {/* {item?.number} */}
                          {isMultidie
                            ? getMapperTeethNumbering(item?.number, numbering) % 100
                            : getMapperTeethNumbering(item?.number, numbering)}
                        </CustomSpan>
                      </StyledNumberingCircle>
                      <CustomSpan width={130} fontSize={14} fontColor="#444" fontWeight={400}>
                        {!indicationItem ? (
                          <T>GLOBAL_UNKNOWN</T>
                        ) : indicationItem?.alias.split('_')[0] === userCustomOption.indication ? (
                          indicationItem?.name
                        ) : (
                          t(`INDICATION_${indicationItem?.alias}`)
                        )}
                      </CustomSpan>
                      <CustomSpan width={80} fontSize={13} fontColor="#444" fontWeight={400}>
                        {!materialItem ? (
                          <T>GLOBAL_UNKNOWN</T>
                        ) : materialItem?.alias.split('_')[0] === userCustomOption.material ? (
                          materialItem.name
                        ) : (
                          t(`MATERIAL_${materialItem?.alias}`)
                        )}
                      </CustomSpan>
                      <CustomSpan
                        fontSize={13}
                        fontColor="#888"
                        fontWeight={400}
                        style={{ width: 20 }}
                      >
                        <div>{materialShadeItem}</div>
                      </CustomSpan>
                      {/* {!isViewer && ( */}
                      <IconButton
                        aria-label="delete_tooth"
                        onClick={(e: any) => !!onDeleteTooth && onDeleteTooth(e, item?.number)}
                      >
                        <img src={icon_trash} />
                      </IconButton>
                      {/* )} */}
                    </div>
                  </AccordionSummary>
                  <AccordionDetails
                    className={cx('selectedTeethList__detail_box', {
                      isFirst: index === 0,
                      isLast: index === teeth?.length - 1,
                    })}
                  >
                    {detailItem?.map(
                      (item: any, index: number) =>
                        !item.isHide && (
                          <div className="selectedTeethList__detail_item" key={index}>
                            <CustomSpan
                              fontSize={13}
                              fontColor="#888"
                              fontWeight={400}
                              className="item_title"
                            >
                              {item?.title}
                            </CustomSpan>
                            <div className="item_content">
                              <CustomSpan
                                fontSize={13}
                                fontColor="#444"
                                fontWeight={400}
                                className="item_title"
                              >
                                {item?.content}
                              </CustomSpan>
                            </div>
                          </div>
                        ),
                    )}
                    {detailOptionItem.map((option: any, index: number) => {
                      if (!detailOptionValue) return;

                      if (option.key in detailOptionValue) {
                        if (!indicationOptionOffline) return;
                        let indicationOption: any = [];

                        indicationOptionOffline?.list?.map((listItem: any) => {
                          if (listItem.indicationAlias === indicationItem?.alias) {
                            if (
                              listItem.indicationAlias === 'FDT' &&
                              listItem.materialAlias !== materialItem?.alias
                            )
                              return;

                            if (option.key in listItem?.default) {
                              indicationOption = listItem.default[option.key]?.values || [];
                            } else if (option.key in listItem?.advanced) {
                              indicationOption = listItem.advanced[option.key]?.values || [];
                            }
                          }
                        });
                        const itemValue = detailOptionValue[option.key]?.value;

                        const indicationContent =
                          indicationOption.length > 0 && (itemValue === 0 || !!itemValue)
                            ? indicationOption?.find((opt: any) => opt.value === itemValue)
                                ?.description || ''
                            : itemValue;

                        return (
                          <div className="selectedTeethList__detail_item" key={index}>
                            <CustomSpan
                              fontSize={13}
                              fontColor="#888"
                              fontWeight={400}
                              className="item_title"
                            >
                              {option.title}
                            </CustomSpan>
                            <div className="item_content">
                              <CustomSpan
                                fontSize={13}
                                fontColor="#444"
                                fontWeight={400}
                                className="item_title"
                              >
                                {indicationContent}
                                {/* <div>{item.content}</div> */}
                              </CustomSpan>
                            </div>
                          </div>
                        );
                      } else {
                        return;
                      }
                    })}
                    {!!implantLibrary &&
                      implantLibraryItem?.map(
                        (item: any, index: number) =>
                          !item.isHide && (
                            <div className="selectedTeethList__detail_item" key={index}>
                              <CustomSpan
                                fontSize={13}
                                fontColor="#888"
                                fontWeight={400}
                                className="item_title"
                              >
                                {item?.title}
                              </CustomSpan>
                              <div className="item_content">
                                <CustomSpan
                                  fontSize={13}
                                  fontColor="#444"
                                  fontWeight={400}
                                  className="item_title"
                                >
                                  {item?.content}
                                </CustomSpan>
                              </div>
                            </div>
                          ),
                      )}
                  </AccordionDetails>
                </MuiAccordion>
                <StyledMidLine />
              </div>
            );
          });
        }, [
          teeth,
          selectedTeethIsExpanded,
          indicationOptionOffline,
          indicationInfo,
          bridge,
          numbering,
        ])}
      </div>
    </StyledSelectedTeethList>
  );
}

const StyledSelectedTeethList = styled.div<{ isViewer: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  /* height: ${({ isViewer }) => (isViewer ? '360px' : '100%')}; */
  height: 270px;

  /* padding-top: 50px; */
  .selectedTeethList__title {
    margin-bottom: 20px;
  }
  .selectedTeethList__box {
    overflow: auto;
    scrollbar-gutter: stable;
    .MuiAccordion-root {
      &.Mui-expanded {
        margin: 0px !important;
      }
    }
    .MuiAccordionSummary-root {
      height: 48px;
      min-height: 48px;
      &.Mui-expanded {
        min-height: 48px;
      }
    }
    .selectedTeethList__container {
      box-shadow: none;
      &:before {
        display: none;
      }
      .selectedTeethList__summary_box {
        padding: 0 !important;
        flex-direction: row-reverse;
        /* &:not(.isFirst) {
        border-top: 1px solid ${color.gray_b5} !important;
      } */
        /* &.isLast {
        border-bottom: 1px solid ${color.gray_b5} !important;
      } */
        .MuiAccordionSummary-content {
          margin-left: 30px;
        }
        .MuiAccordionSummary-expandIcon {
          padding: 0px;
          margin-left: 10px;
        }
        .selectedTeethList__summary_item {
          width: 100%;
          display: flex;
          align-items: center;
          column-gap: 30px;
          .selectedTeethList__bridge_icon {
            position: absolute;
            width: 28px;
            height: 28px;
            display: flex;
            justify-content: center;
            align-items: center;
            bottom: 35px;
            left: 25px;
          }
        }
      }
      .selectedTeethList__detail_box {
        display: flex;
        flex-direction: column;
        padding: 10px 0px 10px 60px;
        row-gap: 8px;
        /* &.isLast {
        border-bottom: 1px solid ${color.gray_b5} !important;
      } */
        .selectedTeethList__detail_item {
          display: flex;
          width: 100%;
          .item_title {
            width: 150px;
          }
          .item_content {
            width: calc(100% - 150px);
          }
        }
      }
    }
  }
`;

const StyledNumberingCircle = styled.div<{ size?: number; color?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: ${({ size }) => size && `${size}`}px;
  height: ${({ size }) => size && `${size}`}px;
  border-radius: ${({ size }) => size && `${size / 2}`}px;
  border: ${({ color }) => color && `1px solid ${color}`};
  /* background-color: ${({ color }) => color && `${color}`}; */
`;

const StyledMidLine = styled.div`
  width: 100%;
  border-bottom: 1px solid #e1e1e7;
`;

export default SelectedTeethList;
